import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import UserManagementTab from './components/UserManagementTab';
import TeamManagementTab from './components/TeamManagementTab';
import MessagesTab from './components/MessagesTab';
import NotificationTab from './components/NotificationTab';
import SendAnnouncementModal from './components/SendAnnouncementModal';
import MeetingsTab from './components/MeetingsTab';
import { getDashboardAnalyticsWithChanges, getRecentSignups } from '../../services/analyticsService';
import AIChatbot from '../../components/ui/AIChatbot';
import DocumentManagementModal from '../../components/ui/DocumentManagementModal';
import { useAuth } from '../../contexts/AuthContext';
import ProblemStatementsTab from './components/ProblemStatementsTab';
import { supabase } from '../../lib/supabase';

const PCFAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentUser, setCurrentUser] = useState(null);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [atRiskTeams, setAtRiskTeams] = useState([]);
  const [loadingAtRisk, setLoadingAtRisk] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchRecentActivities();
    fetchAtRiskTeams();
  }, []);

  useEffect(() => {
    if (userProfile) {
      setCurrentUser({
        id: userProfile?.id,
        name: userProfile?.full_name,
        role: userProfile?.team_role || userProfile?.role,
        email: userProfile?.email,
      });
    }
  }, [userProfile]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use admin's last login timestamp (from userProfile or current time as fallback)
      const adminLastLogin = userProfile?.updated_at ? new Date(userProfile.updated_at) : new Date();
      
      const data = await getDashboardAnalyticsWithChanges(adminLastLogin);
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const signups = await getRecentSignups(10);
      setRecentActivities(signups);
    } catch (err) {
      console.error('Error fetching recent activities:', err);
    }
  };

  const fetchAtRiskTeams = async () => {
    setLoadingAtRisk(true);
    try {
      const { data: teams, error: teamsError } = await supabase?.from('teams')?.select(`
          id,
          team_name,
          team_number,
          created_at,
          team_deliverables (
            id,
            status,
            deliverable_name
          ),
          team_members (
            id
          )
        `);

      if (teamsError) throw teamsError;

      // Analyze teams for risk factors
      const atRiskList = teams?.map(team => {
        const reasons = [];
        const deliverables = team?.team_deliverables || [];
        const memberCount = team?.team_members?.length || 0;
        
        // Check for no deliverables submitted
        if (deliverables?.length === 0) {
          reasons?.push('No deliverables submitted');
        }
        
        // Check for stalled progress (no completed deliverables)
        const completedCount = deliverables?.filter(d => d?.status === 'completed')?.length || 0;
        if (deliverables?.length > 0 && completedCount === 0) {
          reasons?.push('No completed deliverables');
        }
        
        // Check for low team size
        if (memberCount < 3) {
          reasons?.push(`Low team size (${memberCount} members)`);
        }
        
        // Check for team age without progress
        const teamAge = Math.floor((new Date() - new Date(team?.created_at)) / (1000 * 60 * 60 * 24));
        if (teamAge > 14 && deliverables?.length === 0) {
          reasons?.push('No activity for 2+ weeks');
        }
        
        return {
          id: team?.id,
          teamName: team?.team_name,
          teamNumber: team?.team_number,
          reasons,
          memberCount,
          deliverablesCount: deliverables?.length,
          completedCount
        };
      })?.filter(team => team?.reasons?.length > 0)?.sort((a, b) => b?.reasons?.length - a?.reasons?.length);

      setAtRiskTeams(atRiskList);
    } catch (err) {
      console.error('Error fetching at-risk teams:', err);
    } finally {
      setLoadingAtRisk(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/pcf-admin-dashboard' }
  ];

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'people', label: 'People', icon: 'Users' },
    { id: 'teams', label: 'Teams', icon: 'Users' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'notification', label: 'Notification', icon: 'Bell' },
    { id: 'meetings', label: 'Meetings', icon: 'Calendar' },
    { id: 'problems', label: 'Problem Statements', icon: 'FileText' }
  ];

  const keyMetrics = analytics ? [
    {
      title: 'Total Users',
      value: analytics?.totalUsers?.toString() || '0',
      change: analytics?.changes?.users?.displayText || '0%',
      changeType: analytics?.changes?.users?.changeType || 'neutral',
      icon: 'Users',
      color: 'primary',
      description: 'All registered users'
    },
    {
      title: 'Total Teams Formed',
      value: analytics?.teamsFormed?.toString() || '0',
      change: analytics?.changes?.teams?.displayText || '0%',
      changeType: analytics?.changes?.teams?.changeType || 'neutral',
      icon: 'Users',
      color: 'warning',
      description: 'Active participating teams'
    },
    {
      title: 'Deliverables Submitted',
      value: analytics?.deliverablesSubmitted?.toString() || '0',
      change: analytics?.changes?.deliverables?.displayText || '0%',
      changeType: analytics?.changes?.deliverables?.changeType || 'neutral',
      icon: 'Upload',
      color: 'success',
      description: 'Progress through 5 stages'
    },
    {
      title: 'Cities Participating',
      value: analytics?.citiesParticipating?.toString() || '0',
      change: analytics?.changes?.cities?.displayText || '+0',
      changeType: analytics?.changes?.cities?.changeType || 'neutral',
      icon: 'Building2',
      color: 'primary',
      description: 'Ecosystem reach and diversity'
    },
    {
      title: 'Teams At-Risk',
      value: analytics?.atRiskTeamsCount?.toString() || '0',
      change: analytics?.atRiskTeamsCount > 0 ? 'Needs attention' : 'All healthy',
      changeType: analytics?.atRiskTeamsCount > 0 ? 'decrease' : 'neutral',
      icon: 'AlertTriangle',
      color: analytics?.atRiskTeamsCount > 0 ? 'error' : 'success',
      description: 'Teams with risk signals',
      hidePercentage: true
    }
  ] : [];

  const quickActions = [
    {
      title: 'Send Announcement',
      description: 'Broadcast message to all users',
      icon: 'Megaphone',
      action: () => setShowAnnouncementModal(true),
      color: 'primary'
    },
    {
      title: 'Manage Documents',
      description: 'Upload and manage knowledge base for AI Chat bot',
      icon: 'FileText',
      action: () => setShowDocumentModal(true),
      color: 'secondary'
    },
    {
      title: 'Manage Users',
      description: 'View and manage user accounts',
      icon: 'Users',
      action: () => setActiveTab('people'),
      color: 'success'
    },
    {
      title: 'Team Overview',
      description: 'Monitor team activities and progress',
      icon: 'Users',
      action: () => setActiveTab('teams'),
      color: 'warning'
    },
    {
      title: 'View Messages',
      description: 'Check platform communications',
      icon: 'MessageSquare',
      action: () => setActiveTab('messages'),
      color: 'error'
    }
  ];

  const renderDashboardTab = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error text-error rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">Error loading analytics</p>
          </div>
          <p className="text-sm mt-1">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAnalytics}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading && !analytics && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Metrics Grid */}
      {!loading && !error && (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {keyMetrics?.map((metric, index) => (
            <MetricsCard key={index} {...metric} />
          ))}
        </div>
      )}

      {/* At-Risk Teams Section */}
      {!loading && !error && atRiskTeams?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-error" />
              <h3 className="text-base sm:text-lg font-semibold text-foreground">At-Risk Teams</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              iconName="RefreshCw" 
              onClick={fetchAtRiskTeams}
              disabled={loadingAtRisk}
            />
          </div>
          
          {loadingAtRisk ? (
            <div className="space-y-3">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-20 bg-muted rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {atRiskTeams?.map((team) => (
                <div 
                  key={team?.id} 
                  className="p-4 bg-error/5 border border-error/20 rounded-lg hover:bg-error/10 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-sm font-semibold text-foreground">{team?.teamName}</h4>
                        <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                          Team #{team?.teamNumber}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">Risk Factors:</span>
                        </p>
                        <ul className="space-y-1">
                          {team?.reasons?.map((reason, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-xs text-error">
                              <Icon name="AlertCircle" size={14} className="flex-shrink-0 mt-0.5" />
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center space-x-1">
                          <Icon name="Users" size={12} />
                          <span>{team?.memberCount} members</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="FileText" size={12} />
                          <span>{team?.completedCount}/{team?.deliverablesCount} completed</span>
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      onClick={() => setActiveTab('teams')}
                      className="flex-shrink-0"
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {atRiskTeams?.length > 5 && (
            <div className="mt-4 pt-4 border-t border-border">
              <Button
                variant="ghost"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => setActiveTab('teams')}
              >
                View All Teams
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-4 sm:p-6 shadow-soft">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {quickActions?.map((action, index) => (
            <button
              key={index}
              onClick={action?.action}
              className="p-3 sm:p-4 bg-muted/50 hover:bg-muted rounded-lg border border-border transition-all duration-200 hover:shadow-soft text-left"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-${action?.color}/10 flex items-center justify-center mb-3`}>
                <Icon name={action?.icon} size={20} className={`text-${action?.color}`} />
              </div>
              <h4 className="text-sm sm:text-base font-medium text-foreground mb-1">{action?.title}</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">{action?.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-4 sm:p-6 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-foreground">Recent Activity</h3>
          <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={fetchRecentActivities}>
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
        <ActivityFeed activities={recentActivities} loading={loading} onRefresh={fetchRecentActivities} />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab();  
      case 'people':
        return <UserManagementTab />;
      case 'teams':
        return <TeamManagementTab filterByCityOfficial={null} />;
      case 'problems':
        return <ProblemStatementsTab />;
      case 'messages':
        return <MessagesTab messages={[]} onSendMessage={() => {}} teamMembers={[]} currentUser={user} />;
      case 'notification':
        return <NotificationTab />;
      case 'meetings':
        return <MeetingsTab />;
      default:
        return renderDashboardTab();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="pcf-admin"
        userName={currentUser?.name}
        notifications={0}
        customMenu={tabs?.map((tab) => ({
          id: tab?.id,
          label: tab?.label,
          icon: tab?.icon,
          active: tab?.id === activeTab,
          onClick: () => setActiveTab(tab?.id),
        }))}
      />
      <main className="container mx-auto px-4 py-6">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">PCF Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Comprehensive program oversight and management
            </p>
          </div>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Last updated</p>
              <p className="text-sm font-medium text-foreground">
                {currentTime?.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })}
              </p>
            </div>
            <Button 
              variant="outline" 
              iconName="RefreshCw" 
              iconPosition="left"
              onClick={() => {
                fetchAnalytics();
                fetchRecentActivities();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {renderTabContent()}
        </div>
      </main>

      {/* Send Announcement Modal */}
      <SendAnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
      />

      {/* Document Management Modal */}
      <DocumentManagementModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
      />

      {/* AI Chatbot */}
      <AIChatbot userRole="pcf_admin" userId={user?.id} />
    </div>
  );
};

export default PCFAdminDashboard;